import { ProjectSocketContext } from 'contexts/ProjectSocketContext';
import {
  IndicationInfoToothShade,
  IPartner,
  IProjectInfo,
  IToothShade,
} from 'lib/types/projectTypes';
import { IUserProfile } from 'lib/types/userTypes';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useShallowAppSelector } from 'store/hooks';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { isClinicUserSelector } from 'store/modules/user';
import useInput from '../useInput';
import { useProjectFiles } from './useProjectFiles';

export const useProject = () => {
  const {
    language,
    user,
    isClinicUser,
    projectInit: { projectCount: projectCount },
    projectInitSuccess,
    projectData: { projectInfo: projectInfo, newChatCount: newChatCount },
    fetchProjectSuccess,
    fetchProjectFailure,
    projectFilesData: { fileData: fileData },
    projectFilesSuccess,
    indicationInfo: { indicationInfo: indicationInfo },
    indicationInfoSuccess,
    fetchManagerList: { list: managerList },
    fetchRemakeList: { list: remakeList },
    isPmsReady,
    pmsCommand,
    pmsData,
    isPmsOrder,
    editReworkSuccess,
    hasAlarm,
    fetchPartner: { list: partnerList, pagingData: partnerPagingData },
    fetchPartnerSuccess,
    fetchPartnerPending,
    editProjectShareSuccess,
    deleteProjectFileSuccess,
    copyProjectData: { projectCopy: projectCopyCount, projectInfo: projectCopyInfo },
    fetchCopyProjectSuccess,
    isReScan,
    editProjectData,
    editProjectSuccess,
    resetProjectSyncSuccess,
    createProjectSync: {
      payload: { partnerGroupIdx: creatSyncPartenr },
    },
    createProjectSyncSuccess,
  }: {
    language: string;
    user: IUserProfile;
    isClinicUser: boolean;
    projectInit: { projectCount: number };
    projectInitSuccess: boolean;
    projectData: { projectInfo: IProjectInfo; newChatCount: number };
    fetchProjectSuccess: boolean;
    fetchProjectFailure: boolean;
    projectFilesData: { fileData: any };
    projectFilesSuccess: boolean;
    indicationInfo: { indicationInfo: any };
    indicationInfoSuccess: boolean;
    fetchManagerList: { list: any };
    fetchRemakeList: { list: any };
    isPmsReady: boolean;
    pmsCommand: any;
    pmsData: any;
    isPmsOrder: any;
    editReworkSuccess: boolean;
    hasAlarm: any;
    fetchPartner: { list: IPartner[]; pagingData: any };
    fetchPartnerSuccess: boolean;
    fetchPartnerPending: boolean;
    editProjectShareSuccess: boolean;
    deleteProjectFileSuccess: boolean;
    copyProjectData: any;
    fetchCopyProjectSuccess: boolean;
    isReScan: boolean;
    editProjectData: any;
    editProjectSuccess: boolean;
    resetProjectSyncSuccess: boolean;
    createProjectSync: { payload: { partnerGroupIdx: number } };
    createProjectSyncSuccess: boolean;
  } = useShallowAppSelector((state: any) => ({
    language: state.base.language,
    user: state.user.user || {},
    isClinicUser: isClinicUserSelector(state),
    projectInit: state.project.initProject.data || { projectCount: {} },
    projectInitSuccess: state.project.initProject.success,
    projectData: state.project.project.data || { projectInfo: {}, newChatCount: 0 },
    fetchProjectSuccess: state.project.project.success,
    fetchProjectFailure: state.project.project.failure,
    projectFilesData: state.file.projectFiles.data || {},
    projectFilesSuccess: state.file.projectFiles.success,
    indicationInfo: state.util.indicationInfo.data || { indicationInfo: {} },
    indicationInfoSuccess: state.util.indicationInfo.success,
    fetchManagerList: state.employee.managerList.data || {},
    fetchRemakeList: state.util.remakeList.data || {},
    editReworkSuccess: state.project.editProjectRework.success,
    hasAlarm: state.project.hasAlarm,
    fetchPartner: state.partner.syncPartners.data || {},
    fetchPartnerSuccess: state.partner.syncPartners.success,
    fetchPartnerPending: state.partner.syncPartners.pending,
    editProjectShareSuccess: state.file.editProjectShare.success,
    deleteProjectFileSuccess: state.file.deleteProjectFile.success,
    copyProjectData: state.project.copyProject.data || {},
    fetchCopyProjectSuccess: state.project.copyProject.success,
    isReScan: state.base.isReScan,
    editProjectData: state.project.editProject.data,
    editProjectSuccess: state.project.editProject.success,
    resetProjectSyncSuccess: state.project.resetProjectSync.success,
    createProjectSync: state.project.createProjectSync.data || { payload: {} },
    createProjectSyncSuccess: state.project.createProjectSync.success,
    isPmsReady: state.pms.isPmsReady,
    pmsCommand: state.pms.command,
    pmsData: state.pms.pmsData,
    isPmsOrder: state.pms.isPmsOrder,
  }));

  const { scanProcess, scanEdit } = useShallowAppSelector(state => ({
    scanProcess: state.base.scanProcess,
    scanEdit: state.base.scanEdit,
  }));

  const scanFileExists = fileData?.scanFileExists;

  const { t } = useTranslation();
  const { ipcRenderer } = window;
  const history = useHistory();

  const projectTabList: string[] = [t('PROJECT_INFORMATION'), t('FILE_SYNC')];

  const { newProjectChat, isChatOpen } = useContext(ProjectSocketContext);

  const {
    isShowChat,
    setIsShowChat,
    setCurrentTab,
    reworkList,
    setPartnerRefresh,
    setIsOpenNewSyncModal,
    newSyncPartner,
    teeth,
    queryPageTab,
    setQueryPageTab,
  } = useProjectDetailStore();

  // const { handleScanFiilAutoView } = useProjectFiles();
  const { pcode: projectCode } = useParams() as { pcode: string };
  const isNew = !projectInfo?.ownerGroupCode;
  const isOwner = isNew ? isNew : user?.groupCode === projectInfo.ownerGroupCode;
  // const [syncInfo, setSyncInfo] = useState<any | null>(!!projectInfo?.syncInfo ? projectInfo?.syncInfo : null);
  const syncInfo = projectInfo?.syncInfo;
  const timeline = projectInfo?.timeline;
  const projectIndication = projectInfo?.indication;
  const restorationPonticDesign = projectIndication?.restorationPonticDesign;
  const occlusalSurfaceDesign = projectIndication?.occlusalSurfaceDesign;
  const ceramicMetalDesign = projectIndication?.ceramicMetalDesign;
  const materialShadeListData = indicationInfo?.materialShadeList || [];
  const isMultidie = projectInfo?.isMultidie;
  const isPim = projectInfo?.isPIM;

  const tooth = useInput({
    number: null,
  });
  const partnerManager = useInput(0);
  const hasOwnTimeline = timeline?.hasOwnProperty('new') && !!timeline['new'];

  const hasSyncAlarm =
    syncInfo?.syncStatus === 0 &&
    String(syncInfo?.userGroupIdx) === String(user.userGroupIdx) &&
    user.authority <= 1;

  const materialShadeList: any = useMemo(
    () =>
      materialShadeListData?.reduce((acc: IToothShade[], curr: IndicationInfoToothShade) => {
        let currArr = [{ group: curr.id }, ...curr.list];
        return acc.concat(currArr);
      }, []),
    [materialShadeListData],
  );

  const isValidProjectInfo = useMemo(() => {
    return (
      !!projectInfo &&
      Object.keys(projectInfo).length > 0 &&
      !!indicationInfo &&
      Object.keys(indicationInfo).length > 0
    );
  }, [projectInfo]);

  const handleClickProjectTab = (tab: 0 | 1, autoSacn?: boolean) => {
    setCurrentTab(tab);
    if (!!autoSacn) {
      setQueryPageTab(tab);
      // handleScanFiilAutoView();
    }
  };

  const handleClickChat = () => {
    setIsShowChat(!isShowChat);
    isChatOpen.setValue(!isShowChat);
  };

  const handleOpenNewSync = () => {
    setIsOpenNewSyncModal(true);
  };
  const handleCloseNewSync = () => {
    setIsOpenNewSyncModal(false);
    setPartnerRefresh(true);
  };
  const handleConfirmNewSync = () => {
    setIsOpenNewSyncModal(false);
    history.push(
      `/project/creating/${projectInfo?.projectCode}?pathTo=copy&copyPartner=${newSyncPartner}`,
    );
  };

  return {
    user,
    language,
    hasSyncAlarm,
    isClinicUser,
    projectCode,
    projectInfo,
    newChatCount,
    projectIndication,
    fetchProjectSuccess,
    fetchProjectFailure,
    projectFilesSuccess,
    scanFileExists,
    isOwner,
    hasAlarm,
    hasOwnTimeline,
    projectTabList,
    isMultidie,
    tooth,
    indicationInfo,
    indicationInfoSuccess,
    materialShadeList,
    partnerManager,
    managerList,
    reworkList,
    remakeList,
    restorationPonticDesign,
    occlusalSurfaceDesign,
    ceramicMetalDesign,
    editReworkSuccess,
    partnerList,
    partnerPagingData,
    fetchPartnerSuccess,
    fetchPartnerPending,
    editProjectShareSuccess,
    deleteProjectFileSuccess,
    projectCount,
    projectInitSuccess,
    projectCopyCount,
    projectCopyInfo,
    fetchCopyProjectSuccess,
    isReScan,
    editProjectData,
    editProjectSuccess,
    resetProjectSyncSuccess,
    creatSyncPartenr,
    createProjectSyncSuccess,
    isPmsReady,
    pmsCommand,
    pmsData,
    isPmsOrder,
    isValidProjectInfo,
    handleOpenNewSync,
    handleCloseNewSync,
    handleConfirmNewSync,
    handleClickChat,
    handleClickProjectTab,
    isPim,
    scanProcess,
    scanEdit,
  };
};
